//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import NavBar from '../navBar/navBar';
import Breadcrumb from '../navBar/Breadcrumb';
import Sidebar from '../Sidebar/Sidebar';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import TenantDataService from '../../utils/TenantDataService';
import CreationFailedBanner from '../CreationFailedBanner/CreationFailedBanner';
import {
  RegisterUserResponse,
  UserTeamspaces,
  Teamspace,
  TeamspaceIdentifier,
  AnnouncementDto,
} from '../Sidebar/SidebarTypes';
import GlobalAnnouncementBanner from '../GlobalAnnouncementBanner/GlobalAnnouncementBanner';
import { ANNOUNCEMENTS_CHANGED_EVENT } from '../../constants/announcementEvents';
import storageService from '../../utils/storageService';
import { useLoopContext } from '../../LoopContext';
import settings from '../../Config/settings';
import { AnnouncementOffsetProvider } from '../../AnnouncementOffsetContext';

interface UIShellProps {
  page: React.ReactNode;
}

const UIShell = ({ page }: Readonly<UIShellProps>) => {
  const [userTeamspaces, setUserTeamspaces] = useState<TeamspaceIdentifier[]>(
    [],
  );
  const [failedTeamspaces, setFailedTeamspaces] = useState<
    TeamspaceIdentifier[]
  >([]);
  const [bannerOpen, setBannerOpen] = useState<boolean>(false);
  const [initialTeamspaceId, setInitialTeamspaceId] = useState<
    string | undefined
  >(undefined);
  const [hasLoadedInitialTeamspaceId, setHasLoadedInitialTeamspaceId] =
    useState<boolean>(false);
  const [isTeamspaceLoading, setIsTeamspaceLoading] = useState<boolean>(true);
  const [activeAnnouncements, setActiveAnnouncements] = useState<
    AnnouncementDto[]
  >([]);
  const [now, setNow] = useState(() => Date.now());

  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const {
    _setTenantId,
    _setTenantName,
    _setUserHref,
    _setTeamspaceName,
    _setTeamspaceId,
    _setTeamspaceSlug,
    _setTeamspacesUpdated,
    teamspacesUpdated,
    userHref,
    teamspaceId,
  } = useTeamspaceContext();
  const name = useUserAuth().user?.profile?.name;

  const {
    user,
    isAuthenticating,
    isAuthenticated,
    isLoading,
    licenses,
    hasBasicRole,
  } = useUserAuth();
  const hasRegistered = useRef(false);
  const lastRouteTypeRef = useRef<'hidden' | 'normal' | null>(null);
  const { failedLoops } = useLoopContext();
  const prevFailedIdsRef = useRef<Set<string>>(new Set());

  // Clear the dismissed flag when a new failed item appears that wasn't previously known
  const clearDismissedIfNewFailures = (
    currentFailed: { id?: string | null; name?: string }[],
  ) => {
    const currentIdArr = currentFailed
      .map((item) => item.id)
      .filter(Boolean) as string[];
    const hasNewId = currentIdArr.some(
      (id) => !prevFailedIdsRef.current.has(id),
    );
    if (hasNewId && currentIdArr.length > 0) {
      storageService.removeSessionItem(storageService.KEYS.BANNER_DISMISSED);
    }
    prevFailedIdsRef.current = new Set(currentIdArr);
  };

  const hasNoLicense =
    isAuthenticated && hasBasicRole && licenses?.length === 0;
  const hasNoRole = isAuthenticated && !hasBasicRole;

  const isHiddenRoute =
    location.pathname.includes('/solution/') ||
    location.pathname.includes('/embed') ||
    location.pathname.includes('/login') ||
    location.pathname.includes('/logged-out') ||
    location.pathname.startsWith('/link/') ||
    location.pathname.includes('/error/') ||
    location.pathname.includes('/logout') ||
    hasNoLicense ||
    hasNoRole;

  const isInitialTeamspace =
    hasLoadedInitialTeamspaceId &&
    teamspaceId &&
    initialTeamspaceId &&
    teamspaceId === initialTeamspaceId;

  const displayShell = !isHiddenRoute;

  // Memoized so its reference is stable across renders (only rebuilt when the
  // user/auth state changes). This keeps it safe to list in effect dependency
  // arrays without re-triggering those effects on every render.
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  useEffect(() => {
    // tenantId/tenantName are only consumed by shell UI, which is hidden on
    // /embed and /link/* routes. Skip this call there so opening a point product
    // in a new tab doesn't pay for a tenant lookup it never uses.
    if (!isHiddenRoute && isAuthenticated && user?.access_token) {
      tenantDataService.getAllTenants().then((tenants) => {
        // Set tenant id and tenant name
        _setTenantId(tenants[0]?.id);
        _setTenantName(tenants[0]?.name);
      });
    }
  }, [
    isAuthenticated,
    user?.access_token,
    isHiddenRoute,
    tenantDataService,
    _setTenantId,
    _setTenantName,
  ]);

  const registerUser = async () => {
    const response: RegisterUserResponse =
      await tenantDataService.registerUser();
    return response;
  };

  const getTeamspace = async (teamspaceUrl: string) => {
    const response: Teamspace =
      await tenantDataService.invokeHREF(teamspaceUrl);
    return response;
  };

  const getUserTeamspaces = async (userUrl: string | undefined) => {
    const response: UserTeamspaces =
      await tenantDataService.getUserTeamspaces(userUrl);
    return response;
  };

  const getInitialTeamspaceNames = async () => {
    setIsTeamspaceLoading(true);
    const allTeamspaces: TeamspaceIdentifier[] = [];
    const allFailedTeamspaces: TeamspaceIdentifier[] = [];
    // registerUser should be called in TeamspaceContext but it is being called here
    // because we need its response for fetching the user's teamspaces
    try {
      const registerUserResponse = await registerUser();
      _setUserHref(registerUserResponse?._links?.user?.href);
      const userTeamspaces = await getUserTeamspaces(
        registerUserResponse?._links?.user?.href,
      );
      const teamspaceRoles = userTeamspaces?._embedded?.teamspaceRoles ?? [];

      await Promise.all(
        teamspaceRoles.map(async (teamspace) => {
          let teamspaceCompleted = true;
          let firstIncompleteTask = '' as string | undefined;
          const userRole = teamspace?.role ?? '';
          const teamspaceUrlSplit =
            teamspace?._links?.teamspace?.href?.split('/rest');
          const teamspaceResponse = await getTeamspace(teamspaceUrlSplit[1]);
          // When taskstatus is 'DONE', all provisioning tasks have finished.
          // The async provisioning flow may not create tasks for every product
          // in TEAMSPACE_TASK_SEQUENCE, so skip the per-task check in that case.
          if (
            teamspaceResponse?.taskstatus !== 'DONE' &&
            teamspaceResponse?._links?.tasks?.href
          ) {
            const teamspaceTasks = await tenantDataService.invokeHREF(
              teamspaceResponse?._links?.tasks?.href,
            );
            const tasksInSequenceToCheck =
              settings.TEAMSPACE_TASK_SEQUENCE.slice(1);
            teamspaceCompleted = tasksInSequenceToCheck.every(
              (taskSequence) => {
                return teamspaceTasks?._embedded?.teamspacetasks.some(
                  (teamTask) => {
                    return (
                      teamTask.type === taskSequence &&
                      teamTask.status === 'COMPLETED'
                    );
                  },
                );
              },
            );
            firstIncompleteTask = tasksInSequenceToCheck.find(
              (taskSequence) =>
                !teamspaceTasks?._embedded?.teamspacetasks.some(
                  (teamTask) =>
                    teamTask.type === taskSequence &&
                    teamTask.status === 'COMPLETED',
                ),
            );
          }

          if (
            (teamspaceResponse?.taskstatus == 'FAILED' ||
              !teamspaceCompleted) &&
            teamspaceResponse?.name !== 'Initial TeamSpace'
          ) {
            allTeamspaces.push({
              name: teamspaceResponse?.name,
              id: teamspaceResponse?.id,
              slug: teamspaceResponse?.slug,
              failedTask: teamspaceResponse?._links?.failedTask?.href,
              role: userRole,
              completed: teamspaceCompleted,
              incompleteTask: firstIncompleteTask,
              iconColor: teamspaceResponse?.iconColor,
            });
            allFailedTeamspaces.push({
              name: teamspaceResponse?.name,
              id: teamspaceResponse?.id,
              slug: teamspaceResponse?.slug,
              failedTask: teamspaceResponse?._links?.failedTask?.href,
              mode: 'teamspace',
              role: userRole,
              completed: teamspaceCompleted,
              incompleteTask: firstIncompleteTask,
              iconColor: teamspaceResponse?.iconColor,
            });
          } else {
            allTeamspaces.push({
              name: teamspaceResponse?.name,
              id: teamspaceResponse?.id,
              slug: teamspaceResponse?.slug,
              role: userRole,
              completed: teamspaceCompleted,
              iconColor: teamspaceResponse?.iconColor,
            });
          }
        }),
      );

      if (allTeamspaces.length > 0) {
        setInitialTeamspaceId(allTeamspaces[0].id);
        setHasLoadedInitialTeamspaceId(true);
      }
      clearDismissedIfNewFailures(allFailedTeamspaces);
      if (
        allFailedTeamspaces.length > 0 &&
        !bannerOpen &&
        displayShell &&
        storageService.getSessionItem(storageService.KEYS.BANNER_DISMISSED) !==
          'true'
      ) {
        setBannerOpen(true);
      }
      setUserTeamspaces(allTeamspaces);
      setFailedTeamspaces(allFailedTeamspaces);

      // Set current teamspace on initial mount
      // Check if a currently selected teamspace exists in local storage, otherwise default to initial teamspace
      const selectedTeamspaceId = storageService.getLocalItemWithKey(
        storageService.KEYS.TEAMSPACE_ID,
      );
      const selectedTeamspaceName = storageService.getLocalItemWithKey(
        storageService.KEYS.TEAMSPACE_NAME,
      );
      const selectedTeamspaceFound = allTeamspaces.find(
        (teamspace) => teamspace.id === selectedTeamspaceId,
      );
      // Filter out 'Initial TeamSpace' from available teamspaces
      const nonInitialTeamspaces = allTeamspaces.filter(
        (teamspace) => teamspace.name !== 'Initial TeamSpace',
      );

      if (
        selectedTeamspaceId &&
        selectedTeamspaceName &&
        selectedTeamspaceFound &&
        selectedTeamspaceFound.name !== 'Initial TeamSpace'
      ) {
        _setTeamspaceId(selectedTeamspaceId);
        _setTeamspaceName(selectedTeamspaceName);
        _setTeamspaceSlug(selectedTeamspaceFound.slug);
      } else if (nonInitialTeamspaces.length > 0) {
        // Default to first non-'Initial TeamSpace' if available
        _setTeamspaceId(nonInitialTeamspaces[0].id);
        _setTeamspaceName(nonInitialTeamspaces[0].name);
        _setTeamspaceSlug(nonInitialTeamspaces[0].slug);
      } else if (allTeamspaces.length > 0) {
        // If no previously selected teamspace is stored in local storage then default to initial teamspace
        _setTeamspaceId(allTeamspaces[0].id);
        _setTeamspaceName(allTeamspaces[0].name);
        _setTeamspaceSlug(allTeamspaces[0].slug);
      }
    } catch (error) {
      console.error(
        'Failed to retrieve teamspaces that user has access to.',
        error,
      );
    } finally {
      setIsTeamspaceLoading(false);
    }
  };

  const getUpdatedTeamspacesForUser = async () => {
    const allTeamspaces: TeamspaceIdentifier[] = [];
    const allFailedTeamspaces: TeamspaceIdentifier[] = [];

    const userTeamspaces = await getUserTeamspaces(userHref);
    const teamspaceRoles = userTeamspaces?._embedded?.teamspaceRoles ?? [];

    await Promise.all(
      teamspaceRoles.map(async (teamspace) => {
        let teamspaceCompleted = true;
        let firstIncompleteTask = '' as string | undefined;
        const userRole = teamspace?.role ?? '';
        const teamspaceUrlSplit =
          teamspace?._links?.teamspace?.href?.split('/rest');
        const teamspaceResponse = await getTeamspace(teamspaceUrlSplit[1]);
        // When taskstatus is 'DONE', all provisioning tasks have finished.
        // The async provisioning flow may not create tasks for every product
        // in TEAMSPACE_TASK_SEQUENCE, so skip the per-task check in that case.
        if (
          teamspaceResponse?.taskstatus !== 'DONE' &&
          teamspaceResponse?._links?.tasks?.href
        ) {
          const teamspaceTasks = await tenantDataService.invokeHREF(
            teamspaceResponse?._links?.tasks?.href,
          );
          const tasksInSequenceToCheck =
            settings.TEAMSPACE_TASK_SEQUENCE.slice(1);
          teamspaceCompleted = tasksInSequenceToCheck.every((taskSequence) => {
            return teamspaceTasks?._embedded?.teamspacetasks.some(
              (teamTask) =>
                teamTask.type === taskSequence &&
                teamTask.status === 'COMPLETED',
            );
          });

          firstIncompleteTask = tasksInSequenceToCheck.find(
            (taskSequence) =>
              !teamspaceTasks?._embedded?.teamspacetasks.some(
                (teamTask) =>
                  teamTask.type === taskSequence &&
                  teamTask.status === 'COMPLETED',
              ),
          );
        }
        if (
          (teamspaceResponse?.taskstatus == 'FAILED' || !teamspaceCompleted) &&
          teamspaceResponse?.name !== 'Initial TeamSpace'
        ) {
          allTeamspaces.push({
            name: teamspaceResponse?.name,
            id: teamspaceResponse?.id,
            slug: teamspaceResponse?.slug,
            failedTask: teamspaceResponse?._links?.failedTask?.href,
            role: userRole,
            completed: teamspaceCompleted,
            incompleteTask: firstIncompleteTask,
            iconColor: teamspaceResponse?.iconColor,
          });
          allFailedTeamspaces.push({
            name: teamspaceResponse?.name,
            id: teamspaceResponse?.id,
            slug: teamspaceResponse?.slug,
            failedTask: teamspaceResponse?._links?.failedTask?.href,
            mode: 'teamspace',
            role: userRole,
            completed: teamspaceCompleted,
            incompleteTask: firstIncompleteTask,
            iconColor: teamspaceResponse?.iconColor,
          });
        } else {
          allTeamspaces.push({
            name: teamspaceResponse?.name,
            id: teamspaceResponse?.id,
            slug: teamspaceResponse?.slug,
            role: userRole,
            completed: teamspaceCompleted,
            iconColor: teamspaceResponse?.iconColor,
          });
        }
      }),
    );

    clearDismissedIfNewFailures(allFailedTeamspaces);
    if (
      allFailedTeamspaces.length > 0 &&
      !bannerOpen &&
      displayShell &&
      storageService.getSessionItem(storageService.KEYS.BANNER_DISMISSED) !==
        'true'
    ) {
      // If banner is not currently open and UIShell should be displayed then set banner to be visible
      setBannerOpen(true);
    }
    setUserTeamspaces(allTeamspaces);
    setFailedTeamspaces(allFailedTeamspaces);
    _setTeamspacesUpdated(false);
    // Set current teamspace
    // Check if a currently selected teamspace exists in local storage
    const selectedTeamspaceId = storageService.getLocalItemWithKey(
      storageService.KEYS.TEAMSPACE_ID,
    );
    const selectedTeamspaceName = storageService.getLocalItemWithKey(
      storageService.KEYS.TEAMSPACE_NAME,
    );
    const selectedTeamspaceFound = allTeamspaces.find(
      (teamspace) => teamspace.id === selectedTeamspaceId,
    );
    // Filter out 'Initial TeamSpace' from available teamspaces
    const nonInitialTeamspaces = allTeamspaces.filter(
      (teamspace) => teamspace.name !== 'Initial TeamSpace',
    );

    if (
      selectedTeamspaceId &&
      selectedTeamspaceName &&
      selectedTeamspaceFound &&
      selectedTeamspaceFound.name !== 'Initial TeamSpace'
    ) {
      _setTeamspaceId(selectedTeamspaceId);
      _setTeamspaceName(selectedTeamspaceName);
      _setTeamspaceSlug(selectedTeamspaceFound.slug);
    } else if (nonInitialTeamspaces.length > 0) {
      // Default to first non-'Initial TeamSpace' if available
      _setTeamspaceId(nonInitialTeamspaces[0].id);
      _setTeamspaceName(nonInitialTeamspaces[0].name);
      _setTeamspaceSlug(nonInitialTeamspaces[0].slug);
    } else if (allTeamspaces.length > 0) {
      // If no previously selected teamspace is stored in local storage then default to initial teamspace
      _setTeamspaceId(allTeamspaces[0].id);
      _setTeamspaceName(allTeamspaces[0].name);
      _setTeamspaceSlug(allTeamspaces[0].slug);
    }
  };

  // ---------------------------------------------------------------------------
  // Embed teamspace resolution — hidden routes only (/embed, /link/*)
  // ---------------------------------------------------------------------------
  // A point-product embed only needs the CURRENT teamspace's { id, name, slug }
  // to pass context into the iframe. It does NOT need the full teamspace list or
  // per-teamspace completion status — those feed shell UI (teamspace switcher,
  // incomplete-teamspace banner) that is hidden on these routes.
  //
  // To avoid the tenant N+1 (registerUser + getUserTeamspaces + a getTeamspace
  // AND a tasks call per teamspace), we resolve the current teamspace from:
  //   • localStorage        -> teamspaceId + teamspaceName  (last-selected; no TTL)
  //   • one live getTeamspace -> slug (and a refreshed name)
  // The getTeamspace call runs on EVERY open, so id/name/slug are re-validated
  // against the server each time — this is not a stale in-memory cache.
  //
  // ⚠️ CONTRACT / WARNING for future frontend work:
  //   This lightweight path provides ONLY { id, name, slug }. localStorage holds
  //   just id + name. If an embed or point product ever needs MORE teamspace
  //   metadata (role, tenantId, task status, iconColor, links, ...), do NOT try
  //   to read it from localStorage here — it isn't there. Instead either add it
  //   to the embed URL from the opener, fetch it explicitly, or route that case
  //   through the full bootstrap (getInitialTeamspaceNames).
  //   See docs/embed-teamspace-resolution.md.
  //
  // Safety: falls back to the full bootstrap when nothing is cached (cold
  // deep-link) OR when the cached id is stale (getTeamspace fails), so behavior
  // is never worse than the original full-bootstrap path.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getInitialTeamspaceNames
  // is an inline helper with a deep dependency chain; it is intentionally excluded
  // from the deps array because wrapping it would require converting several other
  // inline functions. The one-shot hasRegistered guard in the consuming effect
  // prevents stale-closure issues in practice.
  const resolveTeamspaceForEmbed = useCallback(async () => {
    const storedId = storageService.getLocalItemWithKey(
      storageService.KEYS.TEAMSPACE_ID,
    );
    if (!storedId) {
      await getInitialTeamspaceNames();
      return;
    }
    setIsTeamspaceLoading(true);
    const storedName = storageService.getLocalItemWithKey(
      storageService.KEYS.TEAMSPACE_NAME,
    );
    _setTeamspaceId(storedId);
    if (storedName) _setTeamspaceName(storedName);
    setInitialTeamspaceId(storedId);
    setHasLoadedInitialTeamspaceId(true);
    try {
      const ts: Teamspace = await tenantDataService.invokeHREF(
        '/teamspaces/' + storedId,
      );
      if (ts?.slug) _setTeamspaceSlug(ts.slug);
      if (ts?.name) _setTeamspaceName(ts.name);
      setIsTeamspaceLoading(false);
    } catch {
      // The cached teamspace id is stale (e.g. deleted/renamed) or the lookup
      // failed — self-heal by running the full bootstrap so the embed still
      // resolves a valid teamspace instead of holding a bad id.
      await getInitialTeamspaceNames();
    }
  }, [
    _setTeamspaceId,
    _setTeamspaceName,
    _setTeamspaceSlug,
    tenantDataService,
  ]);

  useEffect(() => {
    const currentRouteType = isHiddenRoute ? 'hidden' : 'normal';
    const routeTypeChanged =
      lastRouteTypeRef.current !== null &&
      lastRouteTypeRef.current !== currentRouteType;

    // Reset hasRegistered if route type changed (e.g., from hidden to normal)
    if (routeTypeChanged) {
      hasRegistered.current = false;
    }

    // Need to wait on user access token to be loaded first before fetching teamspaces
    if (isAuthenticated && user?.access_token && !hasRegistered.current) {
      // On hidden routes (point-product embeds) use the lightweight resolver to
      // avoid the tenant N+1; the full bootstrap only feeds shell UI, which is
      // hidden there.
      if (isHiddenRoute) {
        resolveTeamspaceForEmbed();
      } else {
        getInitialTeamspaceNames();
      }
      hasRegistered.current = true;
      lastRouteTypeRef.current = currentRouteType;
    }
    // getInitialTeamspaceNames/resolveTeamspaceForEmbed are inline helpers
    // recreated each render; this effect intentionally runs once after auth
    // (guarded by hasRegistered), so it is keyed on auth state and route type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.access_token, isHiddenRoute]);

  // Detect when user navigates away from teamspace route.
  // This allows us to refresh teamspaces in case the user navigated away before completing teamspace creation
  // and we need to display the incomplete teamspace to the user.
  useEffect(() => {
    // The teamspace-list refresh only feeds shell UI (teamspace switcher /
    // incomplete-teamspace display), which is hidden on /embed and /link/*.
    // Don't run the (N+1) refresh when navigating into a point product.
    if (
      !isHiddenRoute &&
      prevPathRef.current.startsWith('/teamspace') &&
      !location.pathname.startsWith('/teamspace')
    ) {
      getUpdatedTeamspacesForUser();
    }
    // update ref after checking
    prevPathRef.current = location.pathname;
    // getUpdatedTeamspacesForUser and isHiddenRoute are read fresh on each
    // navigation; this effect is intentionally keyed only on the pathname.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (teamspacesUpdated) {
      getUpdatedTeamspacesForUser();
    }
    // getUpdatedTeamspacesForUser is an inline helper; this effect is an
    // intentional trigger keyed on the teamspacesUpdated flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamspacesUpdated]);

  const fetchAnnouncements = useCallback(() => {
    if (
      !isHiddenRoute &&
      isAuthenticated &&
      user?.access_token &&
      teamspaceId
    ) {
      tenantDataService
        .getActiveAnnouncements(teamspaceId)
        .then((announcements) => {
          setActiveAnnouncements(announcements ?? []);
        })
        .catch(() => {
          setActiveAnnouncements([]);
        });
    }
  }, [
    isAuthenticated,
    user?.access_token,
    teamspaceId,
    isHiddenRoute,
    tenantDataService,
  ]);

  // Fetch active announcements when teamspace changes, then poll every 60s
  useEffect(() => {
    // The announcements banner is part of the shell and is hidden on /embed and
    // /link/* routes, so skip the fetch there — a point product opening in a new
    // tab has no use for it.
    fetchAnnouncements();

    // Poll the server periodically so newly created / expired announcements are
    // picked up without a manual page refresh.
    const intervalId = setInterval(fetchAnnouncements, 60_000);
    return () => clearInterval(intervalId);
  }, [fetchAnnouncements]);

  // Re-fetch announcements when they are created or deleted from the management page
  useEffect(() => {
    const handler = () => {
      fetchAnnouncements();
    };
    window.addEventListener(ANNOUNCEMENTS_CHANGED_EVENT, handler);
    return () =>
      window.removeEventListener(ANNOUNCEMENTS_CHANGED_EVENT, handler);
  }, [fetchAnnouncements]);

  // Client-side expiry timer: schedule a `now` tick at the exact moment the
  // next announcement expires so the banner disappears without waiting for
  // the next server poll.
  useEffect(() => {
    if (activeAnnouncements.length === 0) return;
    const currentNow = Date.now();
    const soonest = activeAnnouncements.reduce<number | null>((min, a) => {
      if (!a.expiresAt) return min;
      const exp = new Date(a.expiresAt).getTime();
      if (exp <= currentNow) return min;
      return min === null ? exp : Math.min(min, exp);
    }, null);
    if (soonest === null) return;
    const delay = soonest - currentNow + 500; // +500ms buffer for clock skew
    const timerId = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(timerId);
  }, [activeAnnouncements]);

  // Filter out announcements that have expired client-side between server polls
  const visibleAnnouncements = useMemo(() => {
    return activeAnnouncements.filter((a) => {
      if (!a.expiresAt) return true;
      return new Date(a.expiresAt).getTime() > now;
    });
  }, [activeAnnouncements, now]);

  useEffect(() => {
    clearDismissedIfNewFailures(failedLoops);
    if (
      failedLoops.length > 0 &&
      displayShell &&
      storageService.getSessionItem(storageService.KEYS.BANNER_DISMISSED) !==
        'true'
    ) {
      setBannerOpen(true);
    }
    // clearDismissedIfNewFailures/displayShell are read fresh; this effect is an
    // intentional trigger keyed on the failedLoops list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedLoops]);

  const hasAnnouncement = !isHiddenRoute && visibleAnnouncements.length > 0;
  const announcementHeight = hasAnnouncement ? 40 : 0;

  return (
    <AnnouncementOffsetProvider value={announcementHeight}>
      <Box
        data-testid={'id-platform-uishell-container'}
        id="id-platform-uiShell-container"
        sx={{ display: 'flex' }}
      >
        {hasAnnouncement && (
          <GlobalAnnouncementBanner announcements={visibleAnnouncements} />
        )}

        {!isHiddenRoute && (
          <NavBar
            showInfoOptions={
              !(
                (!isAuthenticated && isAuthenticating) ||
                (isAuthenticated && isLoading)
              )
            }
            userName={name ?? 'Guest'}
            topOffset={announcementHeight}
          />
        )}

        {/* Sidebar should appear only after at least one non-'Initial TeamSpace' exists and teamspaces have finished loading */}
        {!isHiddenRoute &&
          isAuthenticated &&
          !isTeamspaceLoading &&
          userTeamspaces.some((ts) => ts.name !== 'Initial TeamSpace') && (
            <Sidebar
              userTeamspaces={userTeamspaces}
              topOffset={announcementHeight}
            />
          )}

        <Box
          data-testid={'id-platform-page-container'}
          id="id-platform-page-container"
          sx={{
            flex: 1,
            minHeight: '100vh',
            overflowY: 'hidden',
            overflowX: 'hidden',
          }}
        >
          <div
            id="id-ui-shell-page-wrapper"
            data-testid="ui-shell-page-wrapper"
            className={displayShell ? 'overflow-y-auto' : 'mt-0'}
            style={
              displayShell
                ? {
                    height: `calc(100vh - ${48 + announcementHeight}px)`,
                    paddingTop: `${48 + announcementHeight}px`,
                  }
                : undefined
            }
          >
            <div
              id="id-ui-shell-breadcrumb-wrapper"
              data-testid="ui-shell-breadcrumb-wrapper"
              className="relative"
            >
              {displayShell && <Breadcrumb />}
              <div
                id="id-ui-shell-banner-wrapper"
                data-testid="ui-shell-banner-wrapper"
                className="absolute right-0 top-0 z-10"
              >
                <CreationFailedBanner
                  bannerOpen={displayShell && bannerOpen}
                  setBannerOpen={setBannerOpen}
                  failedTeamspaces={failedTeamspaces}
                  failedLoops={failedLoops}
                />
              </div>
            </div>
            {page}
          </div>
        </Box>
      </Box>
    </AnnouncementOffsetProvider>
  );
};

export default UIShell;
